import type { FC } from 'react'
import { useState } from 'react'

import { Input, Popup, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useDispatch, useSelector } from 'react-redux'

import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { Translation } from '@/components/i18n/Translation'
import { Product } from '@/components/ProductVisualizer'
import { getEcomOrder, postImei } from '@/ducks/ecomOrderReducer'

import { EcomOrderCommunications } from './EcomOrderCommunications'

import './ecom-order-products.scss'

const EcomOrderProducts: FC<{ products: any[]; currency: string; isViewOnlyMode: boolean }> = ({
  products = [],
  currency,
  isViewOnlyMode,
}) => (
  <section>
    <h3>
      <Translation id="Products" />
    </h3>

    <table className="products-table">
      <thead>
        <tr>
          <th colSpan={2}>
            <Translation id="Product" />
          </th>

          <th>
            <Translation id="id" />
          </th>

          <th className="number">
            <Translation id="cost" />
          </th>
        </tr>
      </thead>

      <tbody>
        {products.map(product => (
          <ShopOrderProduct
            key={product.id}
            {...product}
            isViewOnlyMode={isViewOnlyMode}
            currency={currency}
          />
        ))}
      </tbody>
    </table>

    {/* @ts-ignore */}
    <EcomOrderCommunications />
  </section>
)

export default EcomOrderProducts

const ShopOrderProduct: FC<any> = ({
  product_name,
  article_no,
  amount,
  article_image_url,
  quantity,
  category,
  imei,
  currency,
  isViewOnlyMode,
  ...rest
}) => {
  const { whiteLabel } = useAppContext()
  const [isProductPopupOpen, setIsProductPopupOpen] = useState(false)

  return (
    <>
      <tr
        role="button"
        tabIndex={0}
        className="shop-order-product"
        onClick={() => setIsProductPopupOpen(true)}
        onKeyDown={() => setIsProductPopupOpen(true)}
      >
        <td>
          {/* @ts-ignore */}
          <Product imgUrl={article_image_url} />
        </td>

        <td>
          {quantity}x {product_name}
          <div className="product-category">{category}</div>
        </td>

        <td>
          {article_no}

          <div className="product-imei">
            <Translation id="Imei:" />{' '}
            {imei || (
              <StyledText size="small" whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}>
                <Translation id="missing" />
              </StyledText>
            )}
          </div>
        </td>

        <td className="number">
          <FormattedNumberWithCurrency amount={amount * quantity} />
        </td>
      </tr>

      <Popup isOpen={isProductPopupOpen} ariaLabel="product-details">
        <ProductDetails
          isViewOnlyMode={isViewOnlyMode}
          product={{
            product_name,
            article_no,
            amount,
            article_image_url,
            quantity,
            category,
            imei,
            ...rest,
          }}
          currency={currency}
          onClose={() => setIsProductPopupOpen(false)}
        />
      </Popup>
    </>
  )
}

type Product = {
  amount: number
  article_image_url: string
  article_name: string
  article_no: string
  category: string
  email: string
  id: number
  imei: string | null
  is_leasing: boolean
  phone_number: string
  product_name: string
  quantity: number
  user_name: string
  vat_amount: number
  vendor_unique_id: string
}

const ProductDetails: FC<{
  product: Product
  currency: string
  isViewOnlyMode: boolean
  onClose: () => void
}> = ({ product, currency, isViewOnlyMode, onClose }) => {
  const { whiteLabel } = useAppContext()
  const dispatch = useDispatch()
  // Value visible in the modal
  const [locallySelectedImei, setLocallySelectedImei] = useState(product.imei || '')
  const { product_name, vat_amount, is_leasing, article_name, article_no, vendor_unique_id } =
    product
  const order = useSelector(getEcomOrder)

  return (
    <div className="product-details">
      <h2>{product_name}</h2>

      <div className="info">
        <Translation id="Article name" className="info-title" />
        <span className="info-content">{article_name}</span>
      </div>

      <div className="info">
        <Translation id="Article number" className="info-title" />
        <span className="info-content">{article_no}</span>
      </div>

      <div className="info">
        <Translation id="Unique vendor id" className="info-title" />
        <span className="info-content">{vendor_unique_id}</span>
      </div>

      <div className="info">
        <Translation id="Vat amount" className="info-title" />

        <span className="info-content">
          <FormattedNumberWithCurrency amount={vat_amount} currency={currency} />
        </span>
      </div>

      <div className="info">
        <Translation id="Leasing" className="info-title" />
        <span className="info-content">
          {is_leasing ? <Translation id="yes" /> : <Translation id="no" />}
        </span>
      </div>

      <div className="divider" />

      <Input
        id="imei"
        marginBottom={16}
        label={<Translation id="IMEI/ serienummer" />}
        value={locallySelectedImei}
        isDisabled={isViewOnlyMode || order.isInAutomaticProcessing}
        onChange={e => setLocallySelectedImei(e.target.value)}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />

      <footer>
        <Button
          variant="tertiary"
          size="small"
          onClick={() => {
            setLocallySelectedImei(product.imei || '')
            onClose()
          }}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <Translation id="Cancel" />
        </Button>

        <Button
          disabled={isViewOnlyMode || order.isInAutomaticProcessing}
          onClick={() =>
            dispatch(
              // @ts-ignore
              postImei({ imei: locallySelectedImei, productId: product.id, onSuccess: onClose })
            )
          }
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        >
          <Translation id="Update" />
        </Button>
      </footer>
    </div>
  )
}
